import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Coins, MapPin, Pencil, Send } from "lucide-react";
import { toast } from "sonner";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";

export default function Profile() {
  const { id } = useParams();
  const { user, refresh } = useAuth();
  const isOwn = !id || (user && id === user._id);
  const [member, setMember] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  const [form, setForm] = useState({ name: "", family_story: "", baby_names: "", location: "" });
  const [sendForm, setSendForm] = useState({ amount: 5, note: "" });

  const load = useCallback(async () => {
    if (isOwn && user) {
      setMember(user);
      setForm({ name: user.name || "", family_story: user.family_story || "", baby_names: user.baby_names || "", location: user.location || "" });
    } else if (id) {
      const { data } = await api.get(`/members/${id}`);
      setMember(data);
    }
  }, [id, user, isOwn]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      const { data } = await api.put("/profile", form);
      toast.success("Profile updated 💙");
      setEditOpen(false);
      refresh();
      setMember(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const sendTokens = async () => {
    try {
      await api.post("/tokens/send", { to_user_id: member._id, amount: Number(sendForm.amount), note: sendForm.note });
      toast.success(`Sent ${sendForm.amount} AMIR to ${member.name} 💛`);
      setSendOpen(false);
      refresh();
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  if (!member) return <div className="max-w-3xl mx-auto px-5 py-16 text-[#4A5568] font-heading">Loading…</div>;

  const initials = member.name ? member.name.slice(0, 2).toUpperCase() : "?";

  return (
    <div className="max-w-3xl mx-auto px-5 md:px-8 py-12">
      <div className="bg-white border border-[#6A0DAD] rounded-2xl p-8 shadow-sm">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-4">
            <Avatar className="w-20 h-20"><AvatarFallback className="bg-[#6B8EAC] text-white text-2xl font-display">{initials}</AvatarFallback></Avatar>
            <div>
              <h1 className="font-display text-3xl text-[#1A202C]">{member.name}</h1>
              {member.location && <p className="text-[#4A5568] flex items-center gap-1 mt-1"><MapPin size={14} /> {member.location}</p>}
              <span className="mt-2 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#D4AF37]/12 text-[#B8972E] text-sm font-heading font-semibold">
                <Coins size={15} /> {member.amir_balance} AMIR
              </span>
            </div>
          </div>

          {isOwn ? (
            <Dialog open={editOpen} onOpenChange={setEditOpen}>
              <DialogTrigger asChild>
                <button data-testid="edit-profile-btn" className="inline-flex items-center gap-2 bg-white border border-[#6A0DAD] hover:shadow-md text-[#1A202C] rounded-full px-5 py-2.5 font-heading font-semibold transition-all">
                  <Pencil size={16} /> Edit
                </button>
              </DialogTrigger>
              <DialogContent className="rounded-2xl">
                <DialogHeader><DialogTitle className="font-heading">Edit your profile</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <input data-testid="profile-name-input" placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full border border-[#6A0DAD] rounded-xl px-4 py-2.5 outline-none focus:border-[#6B8EAC]" />
                  <input data-testid="profile-babynames-input" placeholder="Baby name(s)" value={form.baby_names} onChange={(e) => setForm({ ...form, baby_names: e.target.value })} className="w-full border border-[#6A0DAD] rounded-xl px-4 py-2.5 outline-none focus:border-[#6B8EAC]" />
                  <input data-testid="profile-location-input" placeholder="Location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="w-full border border-[#6A0DAD] rounded-xl px-4 py-2.5 outline-none focus:border-[#6B8EAC]" />
                  <textarea data-testid="profile-story-input" placeholder="Share your family's story…" rows={5} value={form.family_story} onChange={(e) => setForm({ ...form, family_story: e.target.value })} className="w-full border border-[#6A0DAD] rounded-xl px-4 py-2.5 outline-none focus:border-[#6B8EAC]" />
                </div>
                <DialogFooter>
                  <button data-testid="profile-save-btn" onClick={save} className="bg-[#6B8EAC] text-white hover:bg-[#5A7B96] rounded-full px-6 py-2.5 font-heading font-semibold transition-colors">Save</button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          ) : user ? (
            <Dialog open={sendOpen} onOpenChange={setSendOpen}>
              <DialogTrigger asChild>
                <button data-testid="send-amir-btn" className="inline-flex items-center gap-2 bg-[#D4AF37] text-white hover:bg-[#B8972E] rounded-full px-5 py-2.5 font-heading font-semibold transition-colors shadow-sm">
                  <Send size={16} /> Send AMIR
                </button>
              </DialogTrigger>
              <DialogContent className="rounded-2xl">
                <DialogHeader><DialogTitle className="font-heading">Send AMIR to {member.name}</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <input data-testid="send-amount-input" type="number" min="1" value={sendForm.amount} onChange={(e) => setSendForm({ ...sendForm, amount: e.target.value })} className="w-full border border-[#6A0DAD] rounded-xl px-4 py-2.5 outline-none focus:border-[#6B8EAC]" />
                  <input data-testid="send-note-input" placeholder="Add a note (optional)" value={sendForm.note} onChange={(e) => setSendForm({ ...sendForm, note: e.target.value })} className="w-full border border-[#6A0DAD] rounded-xl px-4 py-2.5 outline-none focus:border-[#6B8EAC]" />
                  <p className="text-xs text-[#4A5568]">Your balance: {user.amir_balance} AMIR</p>
                </div>
                <DialogFooter>
                  <button data-testid="send-confirm-btn" onClick={sendTokens} className="bg-[#D4AF37] text-white hover:bg-[#B8972E] rounded-full px-6 py-2.5 font-heading font-semibold transition-colors">Send</button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          ) : null}
        </div>

        <div className="mt-8">
          {member.baby_names && (
            <p className="font-heading text-[#6B8EAC] font-semibold mb-3">Our little miracle(s): {member.baby_names}</p>
          )}
          <h2 className="font-heading text-sm uppercase tracking-widest text-[#B8972E] mb-2">Our story</h2>
          <p className="text-[#1A202C] leading-relaxed whitespace-pre-wrap">
            {member.family_story || (isOwn ? "You haven't shared your story yet. Click Edit to add it." : "This family hasn't shared their story yet.")}
          </p>
        </div>
      </div>

      {isOwn && (
        <Link to="/wallet" data-testid="view-wallet-link" className="mt-4 block text-center bg-white border border-[#6A0DAD] rounded-2xl py-4 text-[#6B8EAC] font-heading font-semibold hover:shadow-md transition-all">
          View AMIR wallet & transactions →
        </Link>
      )}
    </div>
  );
}
